import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { StageEvent } from '@tailoring-crm/shared';
import { formatDateTime } from '../../lib/format.js';
import { useItemHistory } from '../../lib/workflow/use-kanban.js';

/**
 * Everything that has happened to one garment.
 *
 * This is where "why is this back in cutting?" is answered — with the reason, the person and the
 * time, rather than with somebody's memory of last Tuesday. It is deliberately plain text in
 * reading order: a history that has to be interpreted is a history nobody consults during an
 * argument at the counter.
 *
 * It loads only when opened, because most garments are never asked about.
 */
export function StageHistoryList({
  orderId,
  itemId,
}: {
  orderId: string;
  itemId: string;
}): ReactElement {
  const { t } = useTranslation();
  const history = useItemHistory(orderId, itemId);

  if (history.isPending)
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;

  const events = history.data?.events ?? [];
  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('kanban.historyEmpty')}</p>;
  }

  return (
    <ol className="flex flex-col gap-2 text-sm">
      {events.map((event) => (
        <li key={event.id} className="flex flex-col">
          <span className="font-medium">{describe(event, t)}</span>
          <span className="text-xs text-muted-foreground">
            {formatDateTime(event.at)}
            {' · '}
            {event.actorName
              ? t('kanban.historyBy', { name: event.actorName })
              : t('kanban.historySystem')}
            {event.previousStageMs !== null
              ? ` · ${t('kanban.historyDuration', { duration: humanDuration(event.previousStageMs, t) })}`
              : ''}
          </span>
          {event.reason ? (
            <span className="text-xs italic text-muted-foreground">“{event.reason}”</span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

type Translate = ReturnType<typeof useTranslation>['t'];

function describe(event: StageEvent, t: Translate): string {
  if (event.kind === 'booked') return t('kanban.historyBooked');
  if (event.kind === 'cancelled') return t('kanban.historyCancelled');
  return t('kanban.historyMoved', {
    from: event.fromStage ? t(`stage.${event.fromStage}`) : '',
    to: t(`stage.${event.toStage}`),
  });
}

/** Days, hours or minutes — whichever a person would actually say out loud. */
function humanDuration(ms: number, t: Translate): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return t('duration.minutes', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 48) return t('duration.hours', { count: hours });
  return t('duration.days', { count: Math.round(hours / 24) });
}
